import { type KeyboardEvent, type MouseEvent, type ReactNode, useEffect, useLayoutEffect, useRef } from "react";
import { useCone } from "./cone";
import { fillWords } from "./fill";
import { type ConeVisit, FillCone } from "./FillCone";
import { go } from "./hooks";
import { LevelDot } from "./LevelDot";
import { ShareButton } from "./Share";

/** What the box of a clicked dot shows; `share` undefined: the API gave no fill. */
export interface MapInfo {
  id: string;
  /** The account of the dot: the box shows its small cone (rule U-24). */
  accountId?: string | null;
  title: string;
  name: string;
  share?: number | null;
  /** False for a point with no container, for example a lead. */
  fill?: boolean;
  /** The fill words in place of "About N% full", for example a float reading first. */
  fillText?: string;
  lines?: (string | null | undefined | false)[];
  href?: string | null;
  linkText?: string;
  /** The visit line of the cone in place of the next visit, for example the day of a route plan. */
  visit?: ConeVisit;
  /** A line under the fill words, for example the truck load after a stop (rule U-27). */
  extra?: ReactNode;
  /** Extra data-* attributes for the walk, for example the truck. */
  data?: Record<string, string>;
}

/** A phone sends the click of the tap that opened the box after the box shows, onto what is below the finger. */
export const GHOST_MS = 400;

export const ghostClick = (openedAt: number, now: number) => now - openedAt < GHOST_MS;

/** The one closable box of a clicked dot on each map (rule U-23): the name, the fill and a link to the account. */
export function MapInfoBox({ info, testId, onClose }: { info: MapInfo; testId: string; onClose: () => void }) {
  const box = useRef<HTMLDivElement>(null);
  const openedAt = useRef(0);
  useLayoutEffect(() => {
    openedAt.current = performance.now();
  }, [info.id]);
  useEffect(() => {
    box.current?.focus();
  }, [info.id]);
  const clickCapture = (e: MouseEvent) => {
    if (!ghostClick(openedAt.current, performance.now())) return;
    e.preventDefault();
    e.stopPropagation();
  };
  const keyDown = (e: KeyboardEvent) => {
    if (e.key === "Escape") onClose();
  };
  const data = Object.fromEntries(Object.entries(info.data ?? {}).map(([key, value]) => [`data-${key}`, value]));
  const fill = info.fill !== false;
  const lines = [fill && (info.fillText ?? fillWords(info.share)), ...(info.lines ?? [])].filter(Boolean);
  return (
    <div ref={box} className="map-info-box" role="dialog" aria-labelledby={`${testId}-title`} tabIndex={-1} data-testid={testId} data-id={info.id} onKeyDown={keyDown} onClickCapture={clickCapture} {...data}>
      <p id={`${testId}-title`} className="map-info-box-title">
        <strong>{info.title}</strong>
      </p>
      <span className="map-info-box-tools">
        {info.href && <ShareButton href={info.href} what={info.name} testId={`${testId}-share`} size={18} />}
        <button type="button" className="quiet pin-card-close" aria-label="Close the box" data-testid={`${testId}-close`} onClick={onClose}>
          ✕
        </button>
      </span>
      <p className="map-info-box-name">
        {fill && <LevelDot share={info.share ?? null} small />} <span>{info.name}</span>
      </p>
      {lines.length > 0 && (
        <p className="sub" data-testid={`${testId}-fill`}>
          {lines.join(" · ")}
        </p>
      )}
      {info.extra}
      {info.accountId && fill && <BoxCone accountId={info.accountId} visit={info.visit} />}
      {info.href && (
        <a href={info.href} onClick={go} data-testid={`${testId}-link`}>
          {info.linkText ?? "Open the account"}
        </a>
      )}
    </div>
  );
}

function BoxCone({ accountId, visit }: { accountId: string; visit?: ConeVisit }) {
  const found = useCone(accountId, true);
  if (found === "none") return <p className="sub">No estimate yet.</p>;
  return typeof found === "object" && found !== null ? <FillCone cone={found} size="card" visit={visit} /> : null;
}
